import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useExchangeRates } from '@/hooks/useExchangeRates';
import type { Database } from '@/lib/database.types';

type CategoryBudget = Database['public']['Tables']['category_budgets']['Row'];
type CategoryBudgetInsert = Database['public']['Tables']['category_budgets']['Insert'];
type Category = Database['public']['Tables']['categories']['Row'];
type Transaction = Database['public']['Tables']['transactions']['Row'] & {
  account?: { currency?: string } | null;
};

export interface BudgetWithProgress extends CategoryBudget {
  category?: Category;
  spent: number;
  remaining: number;
  percentage: number;
  isOverBudget: boolean;
}

export function useCategoryBudgets(selectedMonth?: Date) {
  const { user } = useAuth();
  const { currency } = useCurrency();
  const { getRate } = useExchangeRates();
  const [budgets, setBudgets] = useState<CategoryBudget[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get period date range based on selected month
  const getPeriodDateRange = useCallback(
    (period: 'weekly' | 'monthly' | 'yearly') => {
      const ref = selectedMonth ?? new Date();
      let startDate: Date;
      let endDate: Date;

      switch (period) {
        case 'weekly':
          startDate = new Date(ref);
          startDate.setDate(ref.getDate() - ref.getDay());
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(startDate);
          endDate.setDate(startDate.getDate() + 6);
          endDate.setHours(23, 59, 59, 999);
          break;
        case 'monthly':
          startDate = new Date(ref.getFullYear(), ref.getMonth(), 1);
          endDate = new Date(ref.getFullYear(), ref.getMonth() + 1, 0, 23, 59, 59, 999);
          break;
        case 'yearly':
          startDate = new Date(ref.getFullYear(), 0, 1);
          endDate = new Date(ref.getFullYear(), 11, 31, 23, 59, 59, 999);
          break;
      }

      return { startDate, endDate };
    },
    [selectedMonth]
  );

  // Load all data
  const loadData = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      // Load category budgets
      const { data: budgetsData, error: budgetsError } = await supabase
        .from('category_budgets')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (budgetsError) throw budgetsError;

      // Load categories (only visible ones)
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('*')
        .or(`user_id.eq.${user.id},user_id.is.null`)
        .eq('type', 'expense')
        .eq('is_hidden', false)
        .order('display_order');

      if (categoriesError) throw categoriesError;

      // Load transactions for the selected month (for calculating spent amounts)
      const ref = selectedMonth ?? new Date();
      const startOfMonth = new Date(ref.getFullYear(), ref.getMonth(), 1);
      const endOfMonth = new Date(ref.getFullYear(), ref.getMonth() + 1, 0, 23, 59, 59, 999);

      const { data: transactionsData, error: transactionsError } = await supabase
        .from('transactions')
        .select('*, account:accounts!transactions_account_id_fkey(currency)')
        .eq('user_id', user.id)
        .eq('type', 'expense')
        // A pending card payment is not spend yet: the bank can still drop or
        // re-amount it, so counting it would blow a budget on money that never
        // left the account.
        .eq('is_pending', false)
        .gte('date', startOfMonth.toISOString())
        .lte('date', endOfMonth.toISOString())
        .order('date', { ascending: false });

      if (transactionsError) throw transactionsError;

      setBudgets(budgetsData || []);
      setCategories(categoriesData || []);
      setTransactions(transactionsData || []);
    } catch (err) {
      console.error('Error loading category budgets:', err);
      setError(err instanceof Error ? err.message : 'Failed to load budgets');
    } finally {
      setLoading(false);
    }
  }, [user, selectedMonth]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Calculate budget progress for each category
  const budgetsWithProgress = useMemo((): BudgetWithProgress[] => {
    return budgets.map((budget) => {
      const category = categories.find((c) => c.id === budget.category_id);
      const { startDate } = getPeriodDateRange(budget.period as 'weekly' | 'monthly' | 'yearly');

      // Calculate spent amount for this category in current period (exclude display-only recurring)
      const spent = transactions
        .filter((t) => {
          if (t.is_recurring) return false;
          const transactionDate = new Date(t.date);
          return t.category_id === budget.category_id && transactionDate >= startDate;
        })
        .reduce((sum, t) => {
          const acctCurrency = t.account?.currency || 'EUR';
          const rate = getRate(acctCurrency, currency) ?? 1;
          return sum + t.amount * rate;
        }, 0);

      const remaining = Math.max(0, budget.amount_limit - spent);
      const percentage =
        budget.amount_limit > 0 ? Math.min(100, (spent / budget.amount_limit) * 100) : 0;
      const isOverBudget = spent > budget.amount_limit;

      return {
        ...budget,
        category,
        spent,
        remaining,
        percentage,
        isOverBudget,
      };
    });
  }, [budgets, categories, transactions, getPeriodDateRange, currency, getRate]);

  // Get categories without a budget set
  const categoriesWithoutBudget = useMemo(() => {
    const budgetCategoryIds = new Set(budgets.map((b) => b.category_id));
    return categories.filter((c) => !budgetCategoryIds.has(c.id));
  }, [budgets, categories]);

  // Create or update a category budget
  const saveBudget = useCallback(
    async (
      categoryId: string,
      amountLimit: number,
      period: 'weekly' | 'monthly' | 'yearly' = 'monthly'
    ) => {
      if (!user) return { success: false, error: 'Not authenticated' };

      try {
        // Check if budget already exists for this category
        const existingBudget = budgets.find((b) => b.category_id === categoryId);

        if (existingBudget) {
          // Update existing
          const { error } = await supabase
            .from('category_budgets')
            .update({ amount_limit: amountLimit, period, updated_at: new Date().toISOString() })
            .eq('id', existingBudget.id);

          if (error) throw error;
        } else {
          // Create new
          const newBudget: CategoryBudgetInsert = {
            category_id: categoryId,
            amount_limit: amountLimit,
            period,
            user_id: user.id,
          };

          const { error } = await supabase.from('category_budgets').insert(newBudget);

          if (error) throw error;
        }

        await loadData();
        return { success: true };
      } catch (err) {
        console.error('Error saving budget:', err);
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Failed to save budget',
        };
      }
    },
    [user, budgets, loadData]
  );

  // Delete a category budget
  const deleteBudget = useCallback(
    async (budgetId: string) => {
      if (!user) return { success: false, error: 'Not authenticated' };

      try {
        const { error } = await supabase.from('category_budgets').delete().eq('id', budgetId);

        if (error) throw error;

        await loadData();
        return { success: true };
      } catch (err) {
        console.error('Error deleting budget:', err);
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Failed to delete budget',
        };
      }
    },
    [user, loadData]
  );

  // Get total budget stats
  const budgetStats = useMemo(() => {
    const totalBudget = budgetsWithProgress.reduce((sum, b) => sum + b.amount_limit, 0);
    const totalSpent = budgetsWithProgress.reduce((sum, b) => sum + b.spent, 0);
    const overBudgetCount = budgetsWithProgress.filter((b) => b.isOverBudget).length;
    const nearLimitCount = budgetsWithProgress.filter(
      (b) => b.percentage >= 80 && !b.isOverBudget
    ).length;

    return {
      totalBudget,
      totalSpent,
      totalRemaining: Math.max(0, totalBudget - totalSpent),
      overallPercentage: totalBudget > 0 ? Math.min(100, (totalSpent / totalBudget) * 100) : 0,
      overBudgetCount,
      nearLimitCount,
      healthyCount: budgetsWithProgress.length - overBudgetCount - nearLimitCount,
    };
  }, [budgetsWithProgress]);

  return {
    budgets: budgetsWithProgress,
    categories,
    categoriesWithoutBudget,
    budgetStats,
    loading,
    error,
    loadData,
    saveBudget,
    deleteBudget,
  };
}
